import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../models/chart_model";
import { renderHourTick } from "../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../Rajasthan/Utility/const";
import { fetchOptimizerSummary } from "../../../../../Rest_api/RestApi";
import loader from "../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../extensions/date";
import ToggleTabs from "../ChartTableToggle";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../../utility/displayText";

const DataTabs = ["Slot", "Date", "Month"];

interface SummaryData {
  date: string;
  demand: number;
  isgs: number;
  outage: number;
  re: number;
  sgu: number;
  oar: number;
  slot: number;
}

const Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

export default function SimulationSummaryAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedTargetDate,
  data,
  setData,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedTargetDate: any[];
  data: SummaryData[];
  setData: any;
}) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>(DataTabs[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);

  const chartSuffixes = [
    {
      suffix: "demand",
      displayName: "Demand",
      stackSuffix: "demand",
      stroke: "rgba(52, 101, 109, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "oar",
      displayName: "Maximum GNA Requirement",
      stackSuffix: "oar",
      stroke: "rgba(236, 28, 36, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "isgs",
      displayName: "ISGS",
      stackSuffix: "isgs",
      stroke: "rgba(247, 200, 90, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "re",
      displayName: "RE",
      stackSuffix: "re",
      stroke: "rgba(255, 136, 148, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "sgu",
      displayName: "SGU",
      stackSuffix: "sgu",
      stroke: "rgba(99, 159, 203, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "outage",
      displayName: "Outage",
      stackSuffix: "outage",
      stroke: "rgba(244, 127, 89, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
  ];

  const [selectedMetrics] = useState<any[]>(chartSuffixes);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);

  // Sync activeTab with selectedInterval
  useEffect(() => {
    const tabIndex = DataTabs.indexOf(activeTab);
    if (tabIndex !== -1) {
      setSelectedInterval(Intervals[tabIndex]);
    }
  }, [activeTab]);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchOptimizerSummary();
      setUniqueTargetDate(
        Array.from(
          new Set(
            res.map((item: { date: any }) =>
              item.date.split("-").reverse().join("-"),
            ),
          ),
        ),
      );
      setData(res);
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [setError, setUniqueTargetDate]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return selectedBlock.length > 0 ? "slot" : "slot";
    }
    return selectedInterval.key;
  }, [selectedBlock, selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  // Process and filter data based on selected interval
  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      month: getMonthFromDate(item.date),
    }));
    if (selectedTargetDate.length > 0) {
      const types = selectedTargetDate.map(
        (t: any) => t.value.split("-").reverse().join("-") ?? t,
      );
      filteredData = filteredData.filter((row) => types.includes(row.date));
    }
    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["date", "slot"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      }
      return [selectedInterval.values[0]];
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["slot", "date", "month"],
      groupBy: [""],
      volumeKey: "",
      aggregationKeys: ["demand", "isgs", "outage", "re", "sgu", "oar"],
      averageKeys: ["demand", "isgs", "outage", "re", "sgu", "oar"],
      averageDuplicationKeys: ["slot", "date", "month"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedBlock, selectedInterval, selectedTargetDate]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string,
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      legendColor: legendColor,
      type: ["demand", "oar"].includes(suffix)
        ? ChartType.Line
        : selectedChartType.value,
      unit: unit,
      stroke: stroke,
      yAxisId: yAxisId,
      stackId: "a",
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
      }) =>
        chartKeys.map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            chartType,
            unit,
            stroke,
            legendColor,
            yAxisId,
          ),
        ),
    );
  }

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return undefined;
  }

  return (
    <div className="dash-card col-span-1 md:col-span-12 border border-[#718EBF1C] !bg-[#fff] !shadow-[rgba(149,157,165,0.2)_0px_8px_24px] !p-2 !min-h-[100%] w-full">
      <div className="flex justify-between items-center mb-0">
        <h2 className="chartHeading w-60">Demand-Supply Balance</h2>
        <div className="flex items-center gap-3">
          <ToggleTabs
            tabs={DataTabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : (
        <ReMixChart
          tooltipBoundary="viewport"
          chartHeight="calc(100% - 50px)"
          data={chartData}
          showBrush={chartData.length > 96 * 1 ? true : undefined}
          brushStart={BrushStart.Start}
          isTimeSlot={selectedInterval.isTime}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel="MW"
          nameGap={40}
          xDataKey={xDataKey}
          top={10}
          bottom={30}
          xTick={
            selectedInterval.isTime && selectedBlock.length === 0
              ? (value: any, index: any) => renderHourTick(value, 10)
              : undefined
          }
          secondXDataKey={secondDataKey()}
          showToolbox={false}
        />
      )}
    </div>
  );
}
